<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { Ref } from '@/helpers/Decorators';
import { $t } from '@/plugins/localization';
import { Form } from '@/helpers/Form';
import { KeyValuePair } from '@/helpers/Interfaces';
import { nextTick } from 'vue';
import { FrontActionModal } from '@/components/common/dynamic-grid/helpers/GridEnums';
import BpmnService, { BpmnEmitOptions, BpmnResumeFormModel } from '@/modules/low-code/process/services/BpmnService';
import AddAttachments from '@/views/partials/components/attachments/AddAttachments.vue';
import IdeoModal from '@/components/ideo/modal/IdeoModal.vue';

@Options({
    components: { AddAttachments },
})
export default class BPMNAcceptModal extends Vue
{
    @Ref('modal') public modal: () => InstanceType<typeof IdeoModal>;

    public userFilter: KeyValuePair = null;
    public userFilterOptions: KeyValuePair[] = [];

    public form = Form.create<BpmnResumeFormModel>({
        processStep: null,
        targetIds: [],
        status: null,
        comment: '',
        attachments: [],
    });

    public action = Form.create<BpmnEmitOptions>({
        id: '',
        licence: '',
        actionName: '',
        buttonId: '',
        displayMode: 'Modal',
        displaySize: 'Medium',
        title: '',
        buttonName: $t('[[[Akceptuj]]]'),
        buttonVariant: 'success',
        buttonIcon: '',
        groupAction: false,
    });

    public async mounted(): Promise<void>
    {
        await this.$events.$on(FrontActionModal.ResumeModal, async (action: BpmnEmitOptions) =>
        {
            this.action.withData(action);

            this.loadUserFilters();
            await this.loadDetails();
            this.modal().show();
        });
    }

    public unmounted(): void
    {
        this.$events.$off(FrontActionModal.ResumeModal);
    }

    public async loadUserFilters(): Promise<void>
    {
        try
        {
            const result = await BpmnService.getDepartments();

            this.userFilterOptions = result.items?.map?.((item) => item.result);
            this.setDefaultUserFilter();
        }
        catch (ex)
        {
            this.handleException(ex, {
                400: (ex: any) => this.$alert.warning(ex.message),
            });
        }
    }

    public setDefaultUserFilter(): void
    {
        this.userFilter = this.userFilterOptions?.find((item) => item.key === 'Subordinate');
    }

    private async loadDetails(): Promise<boolean>
    {
        try
        {
            const response = await BpmnService.getResumeFormData(this.action.licence, this.action.id);

            this.form.status = response.status;
            this.form.targetIds = response.targetIds;
            this.form.processStep = response.processStep;
        }
        catch (ex)
        {
            this.$log.debug(ex);

            return false;
        }

        return true;
    }

    public async submit(): Promise<void>
    {
        try
        {
            this.form.wait();

            await BpmnService.resume(this.action.id, this.form.formatData(), this.action.licence, this.action.buttonId);
            this.$alert.success(this.$t('[[[Akcja wykonana pomyślnie]]]'));
            this.modal().hide();

            setTimeout(() =>
            {
                this.$emit('reload');
                this.$events.$emit('refetch');
                this.$events.$emit('refetchActions');
                this.$events.$emit('refetchRelatedDocuments');
                this.form.reset();
            }, 500);
        }
        catch (ex)
        {
            this.handleException(ex, {
                400: (ex: any) => this.$alert.warning(ex.message),
                422: (ex: any) => [
                    this.form.$errors.record(ex.data.errors),
                    this.$alert.warning(this.$t('[[[Nie wszystkie pola zostały wypełnione prawidłowo.]]]')),
                ],
            });
        }
        finally
        {
            this.form.continue();
        }
    }

    public async updateUserFilter(): Promise<void>
    {
        this.form.targetIds = [];

        await nextTick();

        if (this.userFilter == null)
        {
            this.setDefaultUserFilter();
        }
    }

    public restartDefault(): void
    {
        this.userFilter = null;
        this.updateUserFilter();
    }
}
</script>

<template>
    <IdeoModal ref="modal" :title="action.title" centered @close="form.reset()" @hidden="form.reset()">
        <template #default>
            <IdeoAlert variant="warning" :show="true" class="mb-3">
                Wprowadzenie poniższych zmian w obiegu dokumentu może mieć wpływ na spójność danych przeliczonych na tym dokumencie lub dokumentach powiązanych.
            </IdeoAlert>
            <IdeoFormGroup
                :label="$t('[[[Krok procesu]]]')"
                :invalid-feedback="form.$errors.first('processStep')"
                :state="form.$errors.state('processStep')"
                required
            >
                <IdeoSelect
                    v-model="form.processStep"
                    :placeholder="$t('[[[Krok procesu]]]')"
                    :endpoint="`simple-dictionary/${action.licence}/form/${action.id}/bpmn-resume/processStep`"
                    :multiselect="false"
                    :clear-button="false"
                    custom-classes="border-radius-right-sm-none"
                    @update:modelValue="form.$errors.clear('processStep')"
                />
            </IdeoFormGroup>
            <IdeoFormGroup
                :label="$t('[[[Użytkownik]]]')"
                :invalid-feedback="form.$errors.first('targetIds')"
                :state="form.$errors.state('targetIds')"
                class="user"
                required
            >
                <div class="row">
                    <!-- :filters="{ userFeatures: department?.key }" -->
                    <IdeoSelect
                        v-model="form.targetIds"
                        :placeholder="$t('[[[Użytkownik]]]')"
                        :endpoint="`simple-dictionary/${action.licence}/form/${action.id}/bpmn-resume/targetIds`"
                        :multiselect="true"
                        :clear-button="false"
                        class="col-sm-6 pe-sm-0"
                        custom-classes="border-radius-right-sm-none"
                        @update:modelValue="form.$errors.clear('targetIds')"
                    >
                        <template #option="{ option }">
                            <UserAvatar
                                :src="$filters.baseurl(`storage/files/${option.pictureUrl}`)"
                                :username="option.value"
                                rounded="circle"
                            />
                            <span class="ms-2 me-1">{{ option.value }}</span>
                            <span v-if="option.unit">({{ option.unit }})</span>
                        </template>
                    </IdeoSelect>
                    <div class="d-flex col-sm-6 ps-sm-0 mt-1 mt-sm-0">
                        <IdeoSelect
                            v-model="userFilter"
                            :placeholder="$t('[[[Dział]]]')"
                            :deselect="false"
                            :options="userFilterOptions"
                            :clear-button="false"
                            class="flex-fill"
                            custom-classes="border-radius-right-none border-radius-left-sm-none"
                            @update:modelValue="updateUserFilter"
                        />
                        <div class="input-group-append">
                            <button
                                type="button"
                                class="btn btn-light border-radius-left-none"
                                @click.stop="restartDefault"
                            >
                                <i class="fas fa-fw fa-xmark"></i>
                            </button>
                        </div>
                    </div>
                </div>
            </IdeoFormGroup>
            <IdeoFormGroup
                :label="$t('[[[Status]]]')"
                :invalid-feedback="form.$errors.first('status')"
                :state="form.$errors.state('status')"
                required
            >
                <IdeoSelect
                    v-model="form.status"
                    :placeholder="$t('[[[Status]]]')"
                    :endpoint="`simple-dictionary/${action.licence}/form/${action.id}/bpmn-resume/status`"
                    :multiselect="false"
                    :clear-button="false"
                    custom-classes="border-radius-right-sm-none"
                    @update:modelValue="form.$errors.clear('status')"
                />
            </IdeoFormGroup>
            <IdeoFormGroup
                :label="$t('[[[Uwagi]]]')"
                :invalid-feedback="form.$errors.first('comment')"
                :state="form.$errors.state('comment')"
            >
                <IdeoFormTextarea v-model="form.comment" rows="6" @input="form.$errors.clear('comment')" />
            </IdeoFormGroup>
        </template>
        <template #modal-footer="{ cancel }">
            <ActionButton
                name="btnConfirmAccept"
                variant="success"
                :text="action.buttonName"
                :confirm="$t('[[[Wprowadzenie  zmian może spowodować utratę spójności jeśli dane z tego dokumentu lub dokumentów powiązanych są przeliczane w trakcie procesowania. Czy na pewno wprowadzić zmiany?]]]')"
                @click="submit"
            />
            <IdeoButton variant="secondary" @click="cancel()">
                {{ $t('[[[Anuluj]]]') }}
            </IdeoButton>
        </template>
    </IdeoModal>
</template>

<style lang="scss" scoped>
.user {
    .user-input {
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
    }
}
</style>
